import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import DetailsTab from "./Forms/Details";
import AddressesTab from "./Forms/Addresses";
import ContactsTab from "./Forms/Contacts";
import sidebar from "../../utils/sidebar";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import FixedSave from "../../../../components/Actions/FixedSave";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { get, post } from "../../../../utils/helpers/api";

function CreateCustomerPage() {
  const { t } = useTranslation();
  const { state } = useLocation();
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;
  const [saveLoading, setSaveLoading] = useState(false);
  const [activeTab, setActiveTab] = useState(1);
  const [customerData, setCustomerData] = useState({
    firstname: state?.customerData?.firstname || "",
    middlename: state?.customerData?.middlename || "",
    lastname: state?.customerData?.lastname || "",
    type: state?.customerData?.type || "",
    company_name: state?.customerData?.company_name || "",
    primary_email: state?.customerData?.primary_email || "",
    primary_phone: state?.customerData?.primary_phone || "",
    country_code: state?.customerData?.country_code || "",
    country: state?.customerData?.country || "",
    notes: state?.customerData?.notes || "",
    registration_number: state?.customerData?.registration_number || "",
    tax_id: state?.customerData?.tax_id || "",
    nis: state?.customerData?.nis || "",
    tax_article: state?.customerData?.tax_article || ""
  });

  const [localAddresses, setLocalAddresses] = useState([]);
  const [localContacts, setLocalContacts] = useState([]);

  useEffect(() => {
    if (id) {
      getCustomer();
    }
  }, [id]);

  const onChangeInput = (target) => {
    setCustomerData({ ...customerData, [target.name]: target.value });
  };

  const getCustomer = async () => {
    const data = await get('/crm/customers/view/' + id);
    if (data?.data) {
      setCustomerData(data.data);
    }
  };

  const addLocalAddress = (data) => {
    setLocalAddresses(prev => [...prev, { ...data, _tempId: Date.now() }]);
  };

  const editLocalAddress = (tempId, data) => {
    setLocalAddresses(prev => prev.map(a => a._tempId === tempId ? { ...data, _tempId: tempId } : a));
  };

  const deleteLocalAddress = (tempId) => {
    setLocalAddresses(prev => prev.filter(a => a._tempId !== tempId));
  };

  const addLocalContact = (data) => {
    setLocalContacts(prev => [...prev, { ...data, _tempId: Date.now() }]);
  };

  const editLocalContact = (tempId, data) => {
    setLocalContacts(prev => prev.map(c => c._tempId === tempId ? { ...data, _tempId: tempId } : c));
  };

  const deleteLocalContact = (tempId) => {
    setLocalContacts(prev => prev.filter(c => c._tempId !== tempId));
  };

  const saveNewCustomer = async () => {
    setSaveLoading(true);
    const createData = await post('/crm/customers/create', { ...customerData });
    if (!createData.success) {
      toast.error(createData.message || t('common.somethingWentWrong'));
      setSaveLoading(false);
      return;
    }
    const newId = createData.customer.id;
    for (const addr of localAddresses) {
      const { _tempId, ...addressData } = addr;
      await post('/crm/customers/addresses/store/' + newId, addressData);
    }
    for (const contact of localContacts) {
      const { _tempId, ...contactData } = contact;
      await post('/crm/customers/contacts/store/' + newId, contactData);
    }
    navigate('/crm/customers');
    setSaveLoading(false);
  };

  const updateCustomer = async () => {
    setSaveLoading(true);
    const data = await post('/crm/customers/update/' + id, { ...customerData });
    if (data.success) {
      navigate("/crm/customers");
    } else {
      toast.error(data.message || t('common.somethingWentWrong'));
    }
    setSaveLoading(false);
  };

  return (
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader
          breadcrumb={[t('crm.customers'), isNew ? t('crm.newCustomer') : t('crm.editCustomer')]}
          onBack={() => navigate('/crm/customers')}
        />

        <div className="subheader-page-content scrollable">
          <Nav tabs>
            <NavItem>
              <NavLink active={activeTab === 1} onClick={() => setActiveTab(1)}>{t('common.description')}</NavLink>
            </NavItem>
            <NavItem>
              <NavLink active={activeTab === 2} onClick={() => setActiveTab(2)}>{t('common.address')}</NavLink>
            </NavItem>
            <NavItem>
              <NavLink active={activeTab === 3} onClick={() => setActiveTab(3)}>{t('crm.contactInformation')}</NavLink>
            </NavItem>
          </Nav>

          <TabContent activeTab={activeTab}>
            <TabPane tabId={1}>
              <DetailsTab customerData={customerData} onChangeInput={onChangeInput} />
            </TabPane>
            <TabPane tabId={2}>
              <AddressesTab
                isNew={isNew}
                customerData={customerData}
                localAddresses={localAddresses}
                onLocalAdd={addLocalAddress}
                onLocalEdit={editLocalAddress}
                onLocalDelete={deleteLocalAddress}
                onSaved={getCustomer}
              />
            </TabPane>
            <TabPane tabId={3}>
              <ContactsTab
                isNew={isNew}
                customerData={customerData}
                localContacts={localContacts}
                onLocalAdd={addLocalContact}
                onLocalEdit={editLocalContact}
                onLocalDelete={deleteLocalContact}
                onSaved={getCustomer}
              />
            </TabPane>
          </TabContent>
        </div>
      </div>

      <FixedSave
        onSave={isNew ? saveNewCustomer : updateCustomer}
        loading={saveLoading}
        disabled={saveLoading || !customerData.firstname}
      />
    </SidebarLayout>
  );
}

export default CreateCustomerPage;
